import React, { useCallback, useEffect, useRef } from "react";
import { Editor as TinymceReact } from "@tinymce/tinymce-react";
import {
  defaultContentCss,
  defaultExternalPlugins,
  defaultMenu,
  defaultOptions,
  defaultPlugins,
  defaultSkinUrl,
  defaultToolbar,
  tinymceScriptSrc
} from "./config";
import { getInternalCopyData, clearInternalCopyData, sanitizePastedHtml } from "./pasteSanitizer";

const ensureTableBorders = (container) => {
  container.querySelectorAll("table").forEach((table) => {
    table.style.borderCollapse = "collapse";
    if (!table.style.border) table.style.border = "1px solid #ccc";
  });
  container.querySelectorAll("td, th").forEach((cell) => {
    if (!cell.style.border) {
      cell.style.border = "1px solid #ccc";
      if (!cell.style.padding) cell.style.padding = "4px 8px";
    }
  });
};

const stripWhitespace = (s) => s.replaceAll(/\s/g, "");

/**
 * Inserts the pasted content through TinyMCE without letting it work on nodes that are not in the editor
 * anymore. The paste listener lives on the body element, so it can still run while the editor is being
 * removed or right after its content was replaced from the value prop - in both cases the stored selection
 * points outside the current body and TinyMCE throws "Node cannot be null or undefined".
 */
const insertContentSafely = (editor, html) => {
  const body = editor.getBody();

  if (editor.removed || !body) {
    return;
  }

  const range = editor.selection?.getRng();
  if (!range || !body.contains(range.startContainer)) {
    editor.selection?.select(body, true);
    editor.selection?.collapse(false);
  }

  try {
    editor.insertContent(html);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("Error inserting the pasted content in TinyMCE:", err);
  }
};

const makeChildStyler = (fontFamily, fontSize, color) => (child) => {
  if (child.nodeType === Node.ELEMENT_NODE) {
    child.style.fontFamily = fontFamily;
    child.style.fontSize = fontSize;
    child.style.color = color;
  } else if (child.nodeType === Node.TEXT_NODE && child.textContent.trim()) {
    const span = document.createElement("span");
    span.style.fontFamily = fontFamily;
    span.style.fontSize = fontSize;
    span.style.color = color;
    child.parentNode.insertBefore(span, child);
    span.appendChild(child);
  }
};

const saveScrollPosition = () => ({
  x: globalThis.scrollX,
  y: globalThis.scrollY
});

const restoreScrollPosition = (pos) => {
  globalThis.scrollTo(pos.x, pos.y);
};

const handleEditorKeyDown = (e) => {
  if (e.key === "Enter") {
    const scrollPos = saveScrollPosition();
    requestAnimationFrame(() => restoreScrollPosition(scrollPos));
  }
};

const handleInternalPaste = (editor, internalData, clipboardText, scrollPos) => {
  const storedText = stripWhitespace(internalData.plainText);
  if (clipboardText !== storedText) {
    return false;
  }
  clearInternalCopyData();
  const wrapper = document.createElement("div");
  wrapper.innerHTML = internalData.html;
  ensureTableBorders(wrapper);
  insertContentSafely(editor, wrapper.innerHTML);
  requestAnimationFrame(() => restoreScrollPosition(scrollPos));
  return true;
};

const handleExternalPaste = (editor, html, scrollPos) => {
  const sanitized = sanitizePastedHtml(html);
  const editorWin = editor.getWin();
  const body = editor.getBody();
  const walker = editorWin.document.createTreeWalker(body, NodeFilter.SHOW_TEXT);
  const firstTextNode = walker.nextNode();
  const refNode = firstTextNode ? firstTextNode.parentElement : body;
  const computed = editorWin.getComputedStyle(refNode);
  const fontFamily = computed.fontFamily;
  const fontSize = firstTextNode ? computed.fontSize : "10pt";
  const color = computed.color;

  const temp = document.createElement("div");
  temp.innerHTML = sanitized;
  ensureTableBorders(temp);
  Array.from(temp.childNodes).forEach(makeChildStyler(fontFamily, fontSize, color));

  insertContentSafely(editor, temp.innerHTML);
  requestAnimationFrame(() => restoreScrollPosition(scrollPos));
};

const handleEditorPaste = (editor, e) => {
  const scrollPos = saveScrollPosition();
  const html = e.clipboardData?.getData("text/html");
  if (!html) {
    requestAnimationFrame(() => restoreScrollPosition(scrollPos));
    return;
  }
  if (editor.removed || !editor.getBody()) return;
  e.preventDefault();
  e.stopImmediatePropagation();

  const internalData = getInternalCopyData();
  if (internalData) {
    const clipboardText = stripWhitespace(e.clipboardData?.getData("text/plain") || "");
    if (handleInternalPaste(editor, internalData, clipboardText, scrollPos)) {
      return;
    }
    clearInternalCopyData();
  }

  handleExternalPaste(editor, html, scrollPos);
};

const TinyMCE = (props) => {
  const { value, onChange, options = {}, showMenuBar = true, onInit, ...rest } = props;
  const editorRef = useRef(null);

  // Cleanup on remove - destroy editor if it is existing
  useEffect(() => {
    return () => {
      try {
        if (editorRef.current) {
          editorRef.current.destroy?.();
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("Error destroying TinyMCE editor on unmount:", err);
      } finally {
        editorRef.current = null;
      }
    };
  }, []);

  // Defend wrapper for onEditorChange (TinyMCE can intern call with node null)
  const safeOnEditorChange = useCallback(
    (content, editor) => {
      try {
        onChange?.(content, editor);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("TinyMCE safeOnEditorChange error:", err);
      }
    },
    [onChange]
  );

  const isTablet = globalThis.window !== undefined && globalThis.innerWidth <= 1023;
  const userSetup = options.setup;

  const initOptions = {
    plugins: defaultPlugins,
    external_plugins: defaultExternalPlugins,
    toolbar: defaultToolbar,
    branding: false,
    skin_url: defaultSkinUrl,
    content_css: defaultContentCss,
    menu: defaultMenu,
    menubar: showMenuBar,
    remove_trailing_brs: true,
    paste_as_text: false,
    paste_preprocess: (_editor, args) => {
      args.content = sanitizePastedHtml(args.content);
    },
    ...defaultOptions,
    ...options,
    setup: (editor) => {
      editor.on("init", () => {
        const editorBody = editor.getBody();
        editorBody.addEventListener("keydown", handleEditorKeyDown, true);
        editorBody.addEventListener("paste", (e) => handleEditorPaste(editor, e), true);
      });
      userSetup?.(editor);
    }
  };

  if (isTablet) {
    const tabletStyle = "body { font-size: 13px !important; }";
    initOptions.content_style = initOptions.content_style ? `${initOptions.content_style} ${tabletStyle}` : tabletStyle;
  }

  return (
    <TinymceReact
      {...rest}
      tinymceScriptSrc={tinymceScriptSrc}
      value={value}
      init={initOptions}
      onEditorChange={safeOnEditorChange}
      onInit={(evt, editor) => {
        editorRef.current = editor;
        onInit?.(evt, editor);
      }}
    />
  );
};

export default TinyMCE;
